/**
 * /chila — "D.I.L.A. // THE CHILA EDIT" — Worn by Ricco Ross.
 *
 * A cinematic editorial capsule that pulls the live Shopify "worn-by-ricco-ross"
 * collection (products, prices, variants and imagery) and lets a customer shop
 * every silhouette one-click WITHOUT leaving the site — mirroring the wash-page
 * rack so multi-item outfits are effortless. Checkout is the only handoff.
 *
 * Layout: Hero editorial node → The Encounter narrative → Capsule grid clusters
 * interleaved with lookbook image drips → footer CTA.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, ShoppingBag, X, Check, Plus } from 'lucide-react';
import api from '../services/api';
import { useCart } from '../context/CartContext';

const COLLECTION = 'worn-by-ricco-ross';
const HERO_FALLBACK = null;

const priceFmt = (n) => `$${parseFloat(n || 0).toFixed(0)}`;
const cleanTitle = (t) => (!t || t === 'Default Title' ? 'One Size' : t);

// Editorial ranking so on-set Ricco / campaign frames float to hero + lookbook.
const imgScore = (u = '') => {
  const s = u.toLowerCase();
  if (/riccoross|summa/.test(s)) return 6;
  if (/family/.test(s)) return 5;
  if (/savant/.test(s)) return 4;
  if (/prsvr[_-]?\d/.test(s)) return 3;
  if (/mirrored|fedoras|valley[_-]?high|taylor/.test(s)) return 2;
  return 0;
};

const Chila = () => {
  const { cart, addManyToCart, removeLineItem, goToCheckout, loading: cartLoading } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({});      // { handle: variantId }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [discount, setDiscount] = useState({ code: 'DILAOUTFIT10', percentage: 10 });

  useEffect(() => {
    Promise.all([
      api.get(`/shopify/collections/${COLLECTION}/full`).then((r) => r.data?.collection?.products || []).catch(() => []),
      api.get('/shopify/dila/discount').then((r) => r.data).catch(() => null),
    ]).then(([prods, disc]) => {
      setProducts(prods);
      if (disc?.code) setDiscount({ code: disc.code, percentage: disc.percentage });
    }).finally(() => setLoading(false));
  }, []);

  // Flat, de-duped editorial image pool from every product gallery.
  const editorial = useMemo(() => {
    const seen = new Set();
    const out = [];
    products.forEach((p) => (p.images || []).forEach((im) => {
      if (im?.url && !seen.has(im.url)) { seen.add(im.url); out.push({ url: im.url, alt: im.alt || p.name }); }
    }));
    return out.sort((a, b) => imgScore(b.url) - imgScore(a.url));
  }, [products]);

  const hero = editorial[0]?.url || HERO_FALLBACK;
  const lookbook = editorial.slice(1, 11);
  const clusters = [lookbook.slice(0, 3), lookbook.slice(3, 6), lookbook.slice(6, 10)];

  // Product clusters mapped per the editorial brief.
  const groupA = products.slice(0, 6);
  const groupB = products.slice(6, 13);
  const groupC = [2, 4, 5, 13, 14].map((i) => products[i]).filter(Boolean);

  const lineIds = useMemo(
    () => new Set((cart?.lineItems || []).map((li) => li.variantId)),
    [cart]
  );
  const cartCount = (cart?.lineItems || []).reduce((t, i) => t + i.quantity, 0);
  const cartSubtotal = (cart?.lineItems || []).reduce(
    (t, i) => t + parseFloat(i.variant?.price?.amount || 0) * i.quantity, 0
  );
  const rate = (discount.percentage || 10) / 100;
  const savedNow = cartCount >= 2 ? cartSubtotal * rate : 0;

  const defaultVariant = (p) => (p.variants || []).find((v) => v.available) || (p.variants || [])[0];

  const quickAdd = async (p) => {
    const single = (p.variants || []).length <= 1;
    const vid = picked[p.handle] || (single ? defaultVariant(p)?.id : null);
    if (!vid) return;
    await addManyToCart([{ variantId: vid, quantity: 1 }]);
  };

  const scrollToCapsule = () => {
    document.getElementById('capsule')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCheckout = () => goToCheckout(cartCount >= 2 ? discount.code : null);

  const ProductCard = ({ p }) => {
    if (!p) return null;
    const variants = p.variants || [];
    const single = variants.length <= 1;
    const anyAvail = variants.some((v) => v.available);
    const chosenId = picked[p.handle] || (single ? defaultVariant(p)?.id : '');
    const chosen = variants.find((v) => v.id === chosenId);
    const img = p.images?.[0]?.url || p.image;
    const inCart = chosen && lineIds.has(chosen.id);
    const addable = anyAvail && (single || (chosen && chosen.available));

    return (
      <div className="group flex flex-col bg-[#141416] border border-white/10 hover:border-[#C9A961]/50 transition-colors" data-testid={`chila-product-${p.handle}`}>
        <div className="relative overflow-hidden bg-[#0b0b0d]">
          {img && (
            <img src={img} alt={p.name} loading="lazy"
              className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.04]" />
          )}
          {!anyAvail && (
            <div className="absolute top-3 right-3 bg-[#0b0b0d] text-white/80 text-[9px] tracking-[0.25em] font-black px-2 py-1 border border-white/20">
              SOLD OUT
            </div>
          )}
          <div className="absolute bottom-3 left-3 text-[9px] tracking-[0.3em] uppercase font-black text-white/70">
            {p.category || 'D.I.L.A.'}
          </div>
        </div>

        <div className="p-4 flex flex-col flex-1">
          <div className="text-sm font-black uppercase tracking-tight leading-tight text-[#f4efe6]">{p.name}</div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mt-1 mb-3">
            From {priceFmt(p.price)}
          </div>

          {!single && (
            <select
              value={chosenId || ''}
              onChange={(e) => setPicked((s) => ({ ...s, [p.handle]: e.target.value }))}
              className="w-full bg-[#0b0b0d] border border-white/15 text-[#f4efe6] px-3 py-2.5 text-xs font-bold uppercase tracking-wider focus:border-[#C9A961] outline-none mb-3"
              data-testid={`chila-variant-${p.handle}`}
            >
              <option value="">Select option</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id} disabled={!v.available}>
                  {cleanTitle(v.title)}{!v.available ? ' — sold out' : ''}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={() => quickAdd(p)}
            disabled={!addable || cartLoading}
            className="mt-auto flex items-center justify-center gap-2 border border-[#C9A961] text-[#C9A961] px-3 py-2.5 text-[11px] font-black tracking-[0.25em] uppercase transition-colors hover:bg-[#C9A961] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#C9A961]"
            data-testid={`chila-add-${p.handle}`}
          >
            {inCart ? (<><Check className="w-3.5 h-3.5" /> Added</>) : (<><Plus className="w-3.5 h-3.5" /> Quick Add</>)}
          </button>
        </div>
      </div>
    );
  };

  const Grid = ({ items }) => (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
      {items.map((p) => <ProductCard key={p.handle} p={p} />)}
    </div>
  );

  // Editorial image drip — asymmetric cascade to break up the grid.
  const Lookbook = ({ imgs }) => {
    if (!imgs || imgs.length === 0) return null;
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:gap-3 my-14" data-testid="chila-lookbook">
        {imgs.map((im, i) => (
          <div
            key={im.url}
            className={`overflow-hidden bg-[#141416] ${i === 0 ? 'col-span-2 md:row-span-2' : ''}`}
          >
            <img src={im.url} alt={im.alt} loading="lazy" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-[#f4efe6]" data-testid="chila-edit">
      {/* Floating cart */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="fixed top-5 right-5 z-40 flex items-center gap-2 bg-[#C9A961] text-black px-4 py-2.5 shadow-lg hover:bg-[#d8bd7f] transition-colors"
        data-testid="chila-cart-button"
      >
        <ShoppingBag className="w-4 h-4" />
        <span className="text-[11px] font-black tracking-[0.2em] uppercase">Bag</span>
        {cartCount > 0 && (
          <span className="bg-black text-[#C9A961] w-5 h-5 rounded-full text-[11px] font-black flex items-center justify-center">
            {cartCount}
          </span>
        )}
      </button>

      {/* ===== SECTION 1: HERO EDITORIAL NODE ===== */}
      <section className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden bg-[#0b0b0d]" data-testid="chila-hero">
        {hero && (
          <img src={hero} alt="D.I.L.A. — The Chila Edit · Ricco Ross"
            className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #0b0b0d 8%, rgba(11,11,13,0.35) 55%, rgba(11,11,13,0.55) 100%)' }} />
        <div className="relative z-10 px-6 lg:px-10 pb-16 lg:pb-24 max-w-6xl mx-auto w-full">
          <Link to="/dila" className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-black text-white/70 hover:text-white mb-8">
            <ArrowLeft className="w-3 h-3" /> D.I.L.A. Design House
          </Link>
          <h1 className="text-4xl sm:text-6xl lg:text-8xl font-black uppercase leading-[0.86] tracking-tight" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            D.I.L.A. <span className="text-[#C9A961]">//</span><br />The Chila Edit
          </h1>
          <p className="mt-6 text-sm sm:text-base tracking-[0.25em] uppercase text-white/70 max-w-2xl">
            A Curation of Silhouettes Selected and Worn by Ricco Ross
          </p>
          <button onClick={scrollToCapsule}
            className="mt-8 inline-flex items-center gap-2 bg-[#C9A961] text-black px-7 py-4 text-xs font-black tracking-[0.3em] uppercase hover:bg-[#d8bd7f] transition-colors"
            data-testid="chila-hero-cta">
            Shop The Capsule <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ===== SECTION 2: THE ENCOUNTER & NARRATIVE ===== */}
      <section className="px-6 lg:px-10 py-24 border-b border-white/10" data-testid="chila-narrative">
        <div className="max-w-4xl mx-auto">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-[#C9A961] mb-8">The Encounter</div>
          <div className="space-y-6 text-base sm:text-lg leading-[1.9] text-[#d9d3c7] font-light">
            <p><span className="text-[#f4efe6] font-medium">A brand isn't defined by the garments it produces; it's validated by the individuals who inhabit them.</span></p>
            <p>Enter Ricco Ross. Currently anchoring screens globally as Horace in Tyler Perry's <em>'Beauty in Black'</em>, Ricco's trajectory is the exact definition of The Chase. He looked at our palm tree insignia and saw his own map: Chicago to Los Angeles. From the frozen concrete of the Midwest to the peak of the industry landscape. A natural transplant.</p>
            <p>When he stepped onto our set, he didn't just wear the pieces — he claimed the narrative, codifying his own journey as <span className="text-[#C9A961] font-black tracking-wide">'CHILA.'</span> We have compiled the exact silhouettes, heavy custom-washed French terries, and structural layers Ricco selected into a highly limited, curated capsule.</p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3 + 4: CAPSULE GRID w/ LOOKBOOK DRIPS ===== */}
      <section id="capsule" className="px-6 lg:px-10 py-20" data-testid="chila-capsule">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase font-black text-[#C9A961] mb-2">The Capsule</div>
              <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-tight">Build The Look</h2>
            </div>
            {cartCount > 0 && (
              <div className="text-[11px] tracking-[0.2em] uppercase font-black text-[#C9A961]" data-testid="chila-savings">
                {cartCount >= 2
                  ? <>You've saved {priceFmt(savedNow)} · {Math.round(discount.percentage)}% bundle applied</>
                  : <>Add one more piece to unlock {Math.round(discount.percentage)}% off</>}
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A961]" /></div>
          ) : products.length === 0 ? (
            <div className="py-32 text-center text-white/50 uppercase tracking-widest text-sm">This edit is resting. Check back soon.</div>
          ) : (
            <>
              <Grid items={groupA} />
              <Lookbook imgs={clusters[0]} />
              <Grid items={groupB} />
              <Lookbook imgs={clusters[1]} />
              <div className="text-[10px] tracking-[0.4em] uppercase font-black text-[#C9A961] mb-6 mt-4">Complete The CHILA Uniform</div>
              <Grid items={groupC} />
              <Lookbook imgs={clusters[2]} />
            </>
          )}
        </div>
      </section>

      {/* ===== SECTION 5: FOOTER CTA ===== */}
      <section className="px-6 lg:px-10 py-28 border-t border-white/10 text-center" data-testid="chila-footer-cta">
        <h2 className="text-4xl sm:text-6xl font-black uppercase tracking-tight" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
          Established<br /><span className="text-[#C9A961]">To Endure.</span>
        </h2>
        <button onClick={scrollToCapsule}
          className="mt-10 inline-flex items-center gap-2 bg-[#C9A961] text-black px-9 py-5 text-xs font-black tracking-[0.3em] uppercase hover:bg-[#d8bd7f] transition-colors"
          data-testid="chila-bundle-cta">
          Shop The Full Uniform Bundle <ArrowRight className="w-4 h-4" />
        </button>
        <div className="mt-14 text-[10px] tracking-[0.35em] uppercase text-white/40">
          prsvr.store · Designed by D.I.L.A.
        </div>
      </section>

      {/* ===== CART DRAWER ===== */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50" data-testid="chila-cart-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#141416] border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="text-sm font-black tracking-[0.3em] uppercase">Your Bag</div>
              <button onClick={() => setDrawerOpen(false)} data-testid="chila-cart-close" className="hover:opacity-60"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cartCount === 0 ? (
                <div className="py-20 text-center text-sm uppercase tracking-widest text-white/40">Your bag is empty.</div>
              ) : (
                <div className="space-y-4">
                  {(cart?.lineItems || []).map((li, idx) => (
                    <div key={li.variantId || idx} className="flex gap-3 border-b border-white/10 pb-4" data-testid={`chila-cart-line-${idx}`}>
                      <div className="w-16 h-20 bg-[#0b0b0d] overflow-hidden shrink-0">
                        {li.variant?.image?.url && <img src={li.variant.image.url} alt={li.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black uppercase tracking-tight leading-tight">{li.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{cleanTitle(li.variant?.title)}</div>
                        <div className="text-xs font-bold mt-1 text-[#C9A961]">{priceFmt(li.variant?.price?.amount)} × {li.quantity}</div>
                      </div>
                      <button onClick={() => removeLineItem(li.variantId)} disabled={cartLoading} className="text-white/40 hover:text-white self-start" data-testid={`chila-cart-remove-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cartCount > 0 && (
              <div className="border-t border-white/10 px-6 py-5 space-y-3">
                <div className="flex justify-between text-sm font-bold">
                  <span className="uppercase tracking-widest">Subtotal</span>
                  <span>${cartSubtotal.toFixed(2)}</span>
                </div>
                {cartCount >= 2 ? (
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#C9A961]">
                    <Check className="w-3.5 h-3.5" /> {Math.round(discount.percentage)}% bundle applied at checkout
                  </div>
                ) : (
                  <div className="text-xs uppercase tracking-wider text-white/50">Add one more piece to save {Math.round(discount.percentage)}%</div>
                )}
                <button onClick={handleCheckout}
                  className="w-full flex items-center justify-center gap-2 bg-[#C9A961] text-black px-6 py-4 text-xs font-black tracking-[0.3em] uppercase hover:bg-[#d8bd7f] transition-colors"
                  data-testid="chila-checkout-btn">
                  Checkout <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => setDrawerOpen(false)} className="w-full text-center text-[11px] font-black tracking-[0.25em] uppercase text-white/50 hover:text-white py-1">
                  Keep Building
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chila;
